import { useId, useState, type ReactNode } from 'react';
import { Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import {
  compactShipmentDistribution,
  type ShipmentDistributionEntry,
} from '../../../utils/shipment-summary';

interface Props {
  title: string;
  entries: ShipmentDistributionEntry[];
  unitLabel: string;
  color: string;
  description?: string;
  controls?: ReactNode;
  rowControls?: ReactNode;
  preserveOrder?: boolean;
  variant?: 'stacked' | 'ranking';
}

export default function ShipmentDistributionRow({
  title,
  entries,
  unitLabel,
  color,
  description,
  controls,
  rowControls,
  preserveOrder = false,
  variant = 'stacked',
}: Props) {
  const { t, currentLanguage } = useTranslation('shipments');
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const locale = currentLanguage.startsWith('en') ? 'en-US' : 'es-CL';
  const sorted = preserveOrder
    ? entries
    : [...entries].sort(
        (a, b) => b.value - a.value || a.label.localeCompare(b.label)
      );
  const total = sorted.reduce((sum, entry) => sum + entry.value, 0);
  const formatValue = (value: number) =>
    value.toLocaleString(locale, { maximumFractionDigits: 2 });
  const formatShare = (value: number) =>
    `${(total > 0 ? (value / total) * 100 : 0).toLocaleString(locale, { maximumFractionDigits: 1 })}%`;
  const formatCompact = (value: number) => {
    const divisor = value >= 1_000_000 ? 1_000_000 : value >= 1_000 ? 1_000 : 1;
    return `${(value / divisor).toLocaleString(locale, { maximumFractionDigits: divisor > 1 ? 1 : 2 })}${divisor === 1_000_000 ? 'M' : divisor === 1_000 ? 'k' : ''}`;
  };
  const preview = compactShipmentDistribution(
    sorted,
    6,
    t('Otros ({{count}})', { count: Math.max(0, sorted.length - 6) })
  );
  const segmentColor = (index: number) =>
    `color-mix(in srgb, ${color} ${Math.max(25, 100 - index * 15)}%, var(--distribution-neutral))`;
  const tooltip = (
    entry: ShipmentDistributionEntry & { members?: ShipmentDistributionEntry[] }
  ) => (
    <div className='max-w-xs space-y-1 px-1 py-0.5 text-xs'>
      <p className='font-semibold break-words'>{entry.label}</p>
      <p>
        {formatValue(entry.value)} {unitLabel} · {formatShare(entry.value)}
      </p>
      <p className='text-default-500'>
        {t('Del total de {{total}} {{unit}}', {
          total: formatValue(total),
          unit: unitLabel,
        })}
      </p>
      {entry.members ? (
        <p className='text-default-500'>
          {entry.members
            .slice(0, 4)
            .map((member) => member.label)
            .join(' · ')}
          {entry.members.length > 4 ? '…' : ''}
        </p>
      ) : null}
      {description ? <p className='text-default-500'>{description}</p> : null}
      <p className='text-default-500'>
        {t('Pulsa para ver el desglose completo')}
      </p>
    </div>
  );
  const toggle = () => setExpanded((value) => !value);

  return (
    <div className='min-w-0 [--distribution-neutral:#e4e4e7] dark:[--distribution-neutral:#3f3f46]'>
      {variant === 'stacked' ? (
        <div className='grid min-h-[60px] grid-cols-1 items-center gap-x-4 gap-y-1 py-1 sm:grid-cols-[144px_minmax(0,1fr)]'>
          <div className='flex min-w-0 flex-col items-start justify-center gap-1'>
            <button
              type='button'
              onClick={toggle}
              aria-expanded={expanded}
              aria-controls={detailsId}
              aria-label={`${title}: ${t(expanded ? 'Ocultar desglose' : 'Ver desglose completo')}`}
              className='flex items-center gap-2 rounded text-sm font-semibold text-default-800 focus-visible:outline-primary'
            >
              {title}
              <Icon icon='tabler:chevron-down'
                aria-hidden='true'
                className={`h-2.5 w-2.5 text-default-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {rowControls ? (
              <div role='group' aria-label={title}>
                {rowControls}
              </div>
            ) : null}
          </div>
          <button
            type='button'
            onClick={toggle}
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={`${title}: ${t(expanded ? 'Ocultar desglose' : 'Ver desglose completo')}`}
            className='group block w-full min-w-0 rounded-lg py-1 text-left outline-none transition-colors hover:bg-default-100/60 focus-visible:ring-2 focus-visible:ring-primary'
          >
            <span className='min-w-0 space-y-2'>
              <span className='flex h-3.5 w-full overflow-hidden rounded-full bg-default-200/70'>
                {preview
                  .filter((entry) => entry.value > 0)
                  .map((entry, index) => (
                    <Tooltip
                      key={entry.label}
                      content={tooltip(entry)}
                      delay={150}
                      closeDelay={0}
                      showArrow
                    >
                      <span
                        aria-label={`${entry.label}: ${formatValue(entry.value)} ${unitLabel}, ${formatShare(entry.value)}`}
                        className='h-full shrink-0 border-r-2 border-content1 last:border-r-0 transition-[filter] hover:brightness-125'
                        style={{
                          width: `${total > 0 ? (entry.value / total) * 100 : 0}%`,
                          background: entry.members
                            ? 'var(--distribution-neutral)'
                            : segmentColor(index),
                        }}
                      />
                    </Tooltip>
                  ))}
              </span>
              <span className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs leading-5 text-default-500'>
                {sorted.slice(0, 3).map((entry, index) => (
                  <Tooltip
                    key={entry.label}
                    content={tooltip(entry)}
                    delay={150}
                    closeDelay={0}
                  >
                    <span className='inline-flex min-w-0 max-w-full items-center gap-1.5'>
                      <span
                        className='h-2 w-2 shrink-0 rounded-full'
                        style={{ background: segmentColor(index) }}
                      />
                      <span className='max-w-[160px] truncate'>
                        {entry.label}
                      </span>
                      <span className='shrink-0 font-mono font-semibold tabular-nums text-default-700'>
                        {formatShare(entry.value)}
                      </span>
                    </span>
                  </Tooltip>
                ))}
                {sorted.length > 3 ? (
                  <span className='whitespace-nowrap'>
                    {t('+{{count}} más', { count: sorted.length - 3 })}
                  </span>
                ) : null}
                {sorted.length === 0 ? (
                  <span>{t('No se encontraron despachos')}</span>
                ) : null}
              </span>
            </span>
          </button>
        </div>
      ) : (
        <div className='space-y-2'>
          {sorted.slice(0, 6).map((entry) => (
            <Tooltip
              key={entry.label}
              content={tooltip(entry)}
              delay={150}
              closeDelay={0}
              showArrow
            >
              <button
                type='button'
                onClick={toggle}
                aria-expanded={expanded}
                aria-controls={detailsId}
                aria-label={`${entry.label}: ${formatValue(entry.value)} ${unitLabel}, ${formatShare(entry.value)}. ${t('Ver desglose completo')}`}
                className='grid w-full grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_auto] items-center gap-3 rounded-md py-1 text-left text-xs outline-none hover:bg-default-100/60 focus-visible:ring-2 focus-visible:ring-primary'
              >
                <span className='truncate text-right text-default-500'>
                  {entry.label}
                </span>
                <span className='h-5 overflow-hidden rounded bg-default-100'>
                  <span
                    className='block h-full rounded'
                    style={{
                      width: `${sorted[0]?.value > 0 ? (entry.value / sorted[0].value) * 100 : 0}%`,
                      background: color,
                    }}
                  />
                </span>
                <span className='min-w-[3.5rem] text-right font-mono font-semibold tabular-nums text-default-800'>
                  {formatCompact(entry.value)}
                </span>
              </button>
            </Tooltip>
          ))}
          {sorted.length === 0 ? (
            <p className='py-5 text-sm text-default-500'>
              {t('No se encontraron despachos')}
            </p>
          ) : null}
          <div className='flex items-center justify-between gap-2 text-xs text-default-500'>
            <button
              type='button'
              onClick={toggle}
              aria-expanded={expanded}
              aria-controls={detailsId}
              className='rounded py-1 text-left hover:text-default-900 focus-visible:outline-primary'
            >
              {expanded
                ? t('Ocultar desglose')
                : sorted.length > 6
                  ? t('+{{count}} más', { count: sorted.length - 6 })
                  : t('Ver desglose completo')}
            </button>
            <span>{unitLabel}</span>
          </div>
        </div>
      )}
      {expanded ? (
        <div
          id={detailsId}
          className='mt-2 space-y-3 rounded-xl border border-default-200 bg-default-50/50 p-3'
        >
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <span className='text-xs font-semibold text-default-700'>
              {title} · {formatValue(total)} {unitLabel}
            </span>
            <button
              type='button'
              onClick={toggle}
              className='rounded text-xs text-default-500 hover:text-default-900 focus-visible:outline-primary'
            >
              {t('Ocultar desglose')}
            </button>
          </div>
          {controls}
          {description ? (
            <p className='text-xs text-default-500'>{description}</p>
          ) : null}
          <div className='max-h-72 overflow-auto'>
            <table className='w-full text-xs'>
              <caption className='sr-only'>{title}</caption>
              <thead className='sticky top-0 bg-content1 text-default-500'>
                <tr>
                  <th scope='col' className='py-2 pr-3 text-left font-medium'>
                    {title}
                  </th>
                  <th scope='col' className='px-2 text-right font-medium'>
                    {unitLabel}
                  </th>
                  <th scope='col' className='pl-2 text-right font-medium'>
                    %
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry, index) => (
                  <tr
                    key={entry.label}
                    className='border-t border-default-200/60 transition-[background-size] duration-200'
                    style={{
                      backgroundImage: `linear-gradient(color-mix(in srgb, ${color} 18%, transparent), color-mix(in srgb, ${color} 18%, transparent))`,
                      backgroundSize: `${total > 0 ? Math.min(100, Math.max(0, (entry.value / total) * 100)) : 0}% 100%`,
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                    <th
                      scope='row'
                      className='py-2 pr-3 text-left font-normal text-default-700'
                    >
                      <span
                        className='inline-block mr-2 h-2 w-2 rounded-full'
                        style={{ background: segmentColor(index) }}
                      />
                      {entry.label}
                    </th>
                    <td className='px-2 text-right font-mono tabular-nums text-default-800'>
                      {formatValue(entry.value)}
                    </td>
                    <td className='pl-2 text-right font-mono tabular-nums text-default-500'>
                      {formatShare(entry.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div id={detailsId} hidden />
      )}
    </div>
  );
}
